/**
 * Generic Global Intersection Observer
 */
import { isEditModeFooter } from 'js/helpers.js';

/**
 * registerIntersectionObserver function to register elements with options and callback func
 * @param {Object} options
 * @param {Function} callback
 */
export default function registerIntersectionObserver(options, callback) {
    if(isEditModeFooter()) return;
    if(!window.IntersectionObserver) return;
    if(!options || typeof options !== 'object') return;
    if(callback && typeof callback !== 'function') return;

    let isIntersectionRegistered = false;
    const intersectionLoadClass = 'intersect-inactive';
    const intersectionActiveClass = 'intersect-active';

    /**
     * Define options
     * - querySelector: DOM element to watch
     * - rootMargin: IntersectionObserver rootMargin
     * - loadClass: class to add to object on DOM Ready (if useDefaultClasses = true)
     * - activeClass: class to add to object when intersected (if useDefaultClasses = true)
     * - useDefaultClasses: uses the default functionality of adding/removing classes on intersect
     */
    const {
        querySelector,
        useDefaultClasses,
        destroyAfter,
    } = options;

    let { rootMargin } = options;

    if(!querySelector) return;
    if(!rootMargin) rootMargin = '-90px';

    // Wrap our functionality so we can call it with conditions later
    function runIntersectionObserver() {
        /**
        * Set Up Intersection Observer Callback
        */
        function intersectCallback(entries) {
            entries.map((entry) => {
                const {
                    target,
                    isIntersecting,
                } = entry;

                let finished = false;

                if(useDefaultClasses) {
                    if(isIntersecting) {
                        target.classList.remove(intersectionLoadClass);
                        target.classList.add(intersectionActiveClass);
                        finished = true;
                    } else {
                        target.classList.remove(intersectionActiveClass);
                        target.classList.add(intersectionLoadClass);
                    }
                }

                if(callback) return callback(entry);
                if(destroyAfter && finished) intersectObserver.unobserve(target);//eslint-disable-line
                return true;
            });
        }

        /**
        * Set Up Observer
        */
        const intersectObserver = new IntersectionObserver(intersectCallback, {
            rootMargin
        });

        /**
        * Observe Elements
        */
        const intersectionElements = document.querySelectorAll(querySelector);
        if(intersectionElements.length <= 0) return;

        [...intersectionElements].map((ele) => {
            ele.classList.add(intersectionLoadClass);
            return intersectObserver.observe(ele);
        });
    }

    // Initialize the IntersectionObserver
    if(!isIntersectionRegistered) {
        runIntersectionObserver();
        isIntersectionRegistered = true;
    }
}
